import { useState } from 'react';
import { HyggeLogo } from '@/components/icons/hygge-logo';
import { GoogleButton } from './google-button';
import { cn } from '@/lib/utils';

interface LoginCardProps {
  error?: string | null;
  onGoogleSignIn?: () => void;
}

export function LoginCard({ error, onGoogleSignIn }: LoginCardProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleSignIn = () => {
    setIsLoading(true);

    if (onGoogleSignIn) {
      onGoogleSignIn();
    } else {
      // Default: redirect to backend OAuth endpoint
      const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
      window.location.href = `${apiUrl}/auth/google`;
    }
  };

  return (
    <div
      className={cn(
        'relative w-full max-w-[420px]',
        'rounded-2xl border border-border bg-card',
        'p-8 sm:p-10',
        'shadow-lg dark:shadow-2xl dark:shadow-black/20',
        'animate-scale-in'
      )}
    >
      {/* Subtle glow effect */}
      <div
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-50"
        style={{
          background: 'linear-gradient(135deg, hsl(var(--hygge) / 0.1) 0%, transparent 50%)',
        }}
      />

      {/* Logo */}
      <div
        className="mb-8 flex justify-center"
        style={{ animationDelay: '100ms' }}
      >
        <HyggeLogo className="h-12" />
      </div>

      {/* Heading */}
      <div className="mb-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Welcome back
        </h1>
      </div>
      <p className="mb-8 text-center text-[15px] text-muted-foreground">
        Sign in to access your HR dashboard
      </p>

      {/* Google Sign In Button */}
      <GoogleButton
        onClick={handleGoogleSignIn}
        isLoading={isLoading}
        disabled={isLoading}
      />

      {/* Error message - aria-live for screen readers */}
      <div
        role="alert"
        aria-live="polite"
        aria-atomic="true"
        className={cn(
          'mt-4 rounded-lg border p-3',
          'text-center text-sm',
          'transition-all duration-200',
          error
            ? 'border-destructive/20 bg-destructive/10 text-destructive opacity-100 animate-slide-up'
            : 'h-0 overflow-hidden border-transparent p-0 opacity-0'
        )}
      >
        {error}
      </div>

      {/* Domain hint */}
      <div className="mt-6 rounded-lg border border-border bg-muted/50 p-3">
        <p className="text-center text-[13px] text-muted-foreground">
          Use your{' '}
          <span className="font-medium text-hygge">@hygge.software</span>{' '}
          account to sign in
        </p>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-[13px] text-muted-foreground">
          © {new Date().getFullYear()} Hygge Software
        </p>
      </div>
    </div>
  );
}

